'use client';

import { Suspense } from 'react';
import dynamic from 'next/dynamic';

import { storyblokEditable } from '@utils/storyblok/storyblokEditable';
import { StoryblokBlockFallback } from '@modules/site/PageFallback';

const Button = dynamic(
  async () => await import('@common/storyblok/Button').then((mod) => mod.Button),
);
const AboutSplitBackground = dynamic(
  async () =>
    await import('@modules/sections/AboutSplitBackground').then((mod) => mod.AboutSplitBackground),
);
const Carousel = dynamic(
  async () => await import('@common/storyblok/Carousel').then((mod) => mod.Carousel),
);
const ContactInformation = dynamic(
  async () =>
    await import('@common/storyblok/ContactInformation').then((mod) => mod.ContactInformation),
);
const Faq = dynamic(async () => await import('@common/storyblok/Faq').then((mod) => mod.Faq));
const CtaImageBand = dynamic(
  async () => await import('@modules/sections/CtaImageBand').then((mod) => mod.CtaImageBand),
);
const FilmCategoryGrid = dynamic(
  async () =>
    await import('@modules/sections/FilmCategoryGrid').then((mod) => mod.FilmCategoryGrid),
);
const FilmVideoShowcase = dynamic(
  async () =>
    await import('@modules/sections/FilmVideoShowcase').then((mod) => mod.FilmVideoShowcase),
);
const Form = dynamic(
  async () => await import('@common/form/FormBuilder').then((mod) => mod.FormBuilder),
);
const FormCheckboxField = dynamic(
  async () => await import('@common/form/FormCheckboxField').then((mod) => mod.FormCheckboxField),
);
const FormDatePickerField = dynamic(
  async () =>
    await import('@common/form/FormDatePickerField').then((mod) => mod.FormDatePickerField),
);
const FormHiddenField = dynamic(
  async () => await import('@common/form/FormHiddenField').then((mod) => mod.FormHiddenField),
);
const FormNumberField = dynamic(
  async () => await import('@common/form/FormNumberField').then((mod) => mod.FormNumberField),
);
const FormPhoneField = dynamic(
  async () => await import('@common/form/FormPhoneField').then((mod) => mod.FormPhoneField),
);
const FormSelectField = dynamic(
  async () => await import('@common/form/FormSelectField').then((mod) => mod.FormSelectField),
);
const FormTextField = dynamic(
  async () => await import('@common/form/FormTextField').then((mod) => mod.FormTextField),
);
const FormTextareaField = dynamic(
  async () => await import('@common/form/FormTextareaField').then((mod) => mod.FormTextareaField),
);
const Grid = dynamic(async () => await import('@common/layout/Grid').then((mod) => mod.Grid));
const GridItem = dynamic(
  async () => await import('@common/layout/GridItem').then((mod) => mod.GridItem),
);
const HeroMedia = dynamic(
  async () => await import('@modules/sections/HeroMedia').then((mod) => mod.HeroMedia),
);
const Image = dynamic(async () => await import('@common/layout/Image').then((mod) => mod.Image));
const Link = dynamic(async () => await import('@common/interaction/Link').then((mod) => mod.Link));
const Marquee = dynamic(
  async () => await import('@common/storyblok/Marquee').then((mod) => mod.Marquee),
);
const Partners = dynamic(
  async () => await import('@common/storyblok/Partners').then((mod) => mod.Partners),
);
const RichText = dynamic(
  async () => await import('@common/typography/RichText').then((mod) => mod.RichText),
);
const RichTextSection = dynamic(
  async () => await import('@modules/sections/RichTextSection').then((mod) => mod.RichTextSection),
);
const Section = dynamic(
  async () => await import('@common/layout/Section').then((mod) => mod.Section),
);
const Tabs = dynamic(async () => await import('@common/storyblok/Tabs').then((mod) => mod.Tabs));
const Testimonials = dynamic(
  async () => await import('@modules/sections/Testimonials').then((mod) => mod.Testimonials),
);
const TwoColIntroImages = dynamic(
  async () =>
    await import('@modules/sections/TwoColIntroImages').then((mod) => mod.TwoColIntroImages),
);
const Video = dynamic(async () => await import('@common/layout/Video').then((mod) => mod.Video));

type ClientStoryblokComponentProps = {
  blok: any;
};

export function ClientStoryblokComponent({ blok }: ClientStoryblokComponentProps) {
  return (
    <Suspense fallback={<StoryblokBlockFallback component={blok.component} />}>
      <ResolvedClientStoryblokComponent blok={blok} />
    </Suspense>
  );
}

function ResolvedClientStoryblokComponent({ blok }: ClientStoryblokComponentProps) {
  switch (blok.component) {
    case 'AboutSplitBackground':
      return <AboutSplitBackground {...blok} />;
    case 'Button':
      return <Button {...blok} />;
    case 'Carousel':
      return <Carousel {...blok} />;
    case 'ContactInformation':
      return <ContactInformation {...blok} />;
    case 'CtaImageBand':
      return <CtaImageBand {...blok} />;
    case 'ExternalVideo':
      return <Video {...blok} />;
    case 'Faq':
      return <Faq {...blok} />;
    case 'FilmCategoryGrid':
      return <FilmCategoryGrid {...blok} />;
    case 'FilmVideoShowcase':
      return <FilmVideoShowcase {...blok} />;
    case 'Form':
      return <Form {...blok} />;
    case 'FormCheckboxField':
      return <FormCheckboxField {...blok} />;
    case 'FormDatePickerField':
      return <FormDatePickerField {...blok} />;
    case 'FormHiddenField':
      return <FormHiddenField {...blok} />;
    case 'FormNumberField':
      return <FormNumberField {...blok} />;
    case 'FormPhoneField':
      return <FormPhoneField {...blok} />;
    case 'FormSelectField':
      return <FormSelectField {...blok} />;
    case 'FormTextField':
      return <FormTextField {...blok} />;
    case 'FormTextareaField':
      return <FormTextareaField {...blok} />;
    case 'Grid':
      return <Grid {...blok} />;
    case 'GridItem':
      return <GridItem {...blok} />;
    case 'HeroMedia':
      return <HeroMedia {...blok} />;
    case 'Image':
    case 'InlineImage':
      return <Image {...blok} />;
    case 'Link':
      return <Link {...blok} />;
    case 'Marquee':
      return <Marquee {...blok} />;
    case 'Partners':
      return <Partners {...blok} />;
    case 'RichText':
      return (
        <RichText
          {...storyblokEditable(blok)}
          {...blok}
        />
      );
    case 'RichTextSection':
      return <RichTextSection {...blok} />;
    case 'Section':
      return <Section {...blok} />;
    case 'Tabs':
      return <Tabs {...blok} />;
    case 'Testimonials':
      return <Testimonials {...blok} />;
    case 'TwoColIntroImages':
      return <TwoColIntroImages {...blok} />;
    default:
      return null;
  }
}
